import {
  createContext,
  createRef,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { noop } from 'lodash'
import { SnackbarKey, SnackbarProvider } from 'notistack'

import CloseIcon from '@mui/icons-material/Close'
import { IconButton, PaletteMode } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { resetAllCheckboxTableStores } from '@/stores/useTableCheckboxStore/useTableCheckboxStore'
import { getTheme, Theme } from '@/styles/theme'

import { getPersistedThemeMode, persistThemeMode } from './memorizeTheme'

const DEFAULT_PALETTE_MODE: PaletteMode = 'light'

export type ConsoleContextThemeType = {
  theme: Theme
  toggleThemeMode: () => void
}

const ConsoleThemeContext = createContext<ConsoleContextThemeType>({
  theme: getTheme(DEFAULT_PALETTE_MODE),
  toggleThemeMode: noop
})

export const useThemeContext = () => useContext(ConsoleThemeContext)

export const ConsoleThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(
    () => getPersistedThemeMode() || DEFAULT_PALETTE_MODE
  )
  const theme = useMemo(() => getTheme(mode), [mode])

  const toggleThemeMode = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'))
  }

  useEffect(() => {
    const persistedMode = getPersistedThemeMode()

    if (!persistedMode) {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        setMode('dark')
      } else {
        setMode('light')
      }
    } else {
      setMode(persistedMode)
    }

    return () => {
      resetAllCheckboxTableStores()
    }
  }, [])

  useEffect(() => {
    persistThemeMode(mode)
  }, [mode])

  const notistackRef = createRef<SnackbarProvider>()
  const onClickDismiss = (key: SnackbarKey) => (): void => {
    if (notistackRef.current) {
      notistackRef.current.closeSnackbar(key)
    }
  }

  return (
    <ConsoleThemeContext.Provider
      value={{
        theme,
        toggleThemeMode
      }}
    >
      <ThemeProvider theme={theme}>
        <SnackbarProvider
          maxSnack={4}
          autoHideDuration={3500}
          preventDuplicate
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          ref={notistackRef}
          action={(key) => (
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={onClickDismiss(key)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          )}
        >
          {children}
        </SnackbarProvider>
      </ThemeProvider>
    </ConsoleThemeContext.Provider>
  )
}
